import { FileMeta } from '@service-victoria/component-library/components/FileUpload/props';
import type { Props as AddressProps } from '@service-victoria/component-library/components/AddressLookup';

import { CSVErrorType, VoucherErrorType } from './api';

export enum Availability {
  Open = 'OPEN',
  TooEarly = 'TOO_EARLY',
  TooLate = 'TOO_LATE',
  Exhausted = 'EXHAUSTED',
  TechnicalError = 'TECHNICAL_ERROR'
}

export enum RedemptionType {
  Individual = 'Individual',
  Bulk = 'Bulk'
}

export enum TransactionType {
  ProviderDetails = 'provider-details',
  Redemption = 'redemption'
}

export enum RedeemStep {
  Select = 'redeem',
  Individual = 'redeem-individual',
  Bulk = 'redeem-bulk'
}

export const RedemptionTypeStepMapping = {
  [RedemptionType.Individual]: RedeemStep.Individual,
  [RedemptionType.Bulk]: RedeemStep.Bulk
};

export interface FileMapping {
  filename: string;
  uuid: string;
}

export interface Address {
  fullDetails: AddressProps.Interfaces.AddressData['fullDetails'] & {
    apiFullAddress?: AddressProps.Interfaces.ApiFullAddress;
  };
  summaryData?: AddressProps.Interfaces.AddressData['summaryData'];
}

export type DecodedIdToken = {
  rawToken: string;
  customerId: string;
  givenName: string;
  familyName: string;
  email: string;
  phoneNumber?: string;
};

export type Attachments = {
  fileUpload: FileMeta[];
  fileMapping: FileMapping[];
};

export type BulkAggregateSummary = {
  totalVouchers: number;
  totalAmount: number;
  activityTypes: string[];
};

export type Redeem = {
  redemptionType: RedemptionType | '';
  individual: {
    voucherCode: string;
    childName?: string;
    activityType: string;
    amount: string;
    isValid?: boolean;
    providerName?: string;
  };
  bulk: Attachments & {
    aggregateData?: BulkAggregateSummary;
  };
};

export type Review = {
  consentDeclared?: boolean;
};

export type RedemptionFormValues = {
  redeem: Redeem;
  review: Review;
};

export type ProviderFormValues = {
  providerDetails: ProviderDetails;
};

export type Transaction = {
  ID: string;
  startTs?: number;
};

export type ProviderDetails = {
  providerName: string;
  activityType: string;
  providerPhone: string;
  address: Address;
  role: string;
};

export const VoucherErrorMessage = {
  [VoucherErrorType.DUPLICATE_VOUCHER]:
    'This voucher code appears more than once in your file. Remove duplicate voucher codes and re-upload.',
  [VoucherErrorType.VOUCHER_NOT_FOUND]:
    "We can't find that voucher. Check you've entered the voucher code correctly.",
  [VoucherErrorType.EMPTY_RECORD]: 'The voucher code is missing.',
  [VoucherErrorType.VOUCHER_REDEEMED]:
    "You've already redeemed that voucher. A voucher cannot be redeemed multiple times.",
  [VoucherErrorType.VOUCHER_CANCELLED]:
    "Sorry, that voucher has been cancelled. Check you've entered the voucher code correctly.",
  [VoucherErrorType.VOUCHER_EXPIRED]:
    "Sorry, that voucher is expired. Check you've entered the voucher code correctly.",
  [VoucherErrorType.INVALID_PROVIDER]:
    'Sorry, your Get Active Kids activity provider is not authorised to redeem vouchers.',
  [VoucherErrorType.SERVER_ERROR]:
    "Sorry, we've lost connection to the service. Please wait a moment and try again.",
  [VoucherErrorType.USER_UNAUTHORIZED]:
    "That voucher doesn't belong to your organisation. Check you've entered the voucher code correctly.",
  [VoucherErrorType.INVALID_AMOUNT]:
    'The redemption amount must be between 0 and 200, with no more than 2 decimal places.'
};

export const CSVErrorMessage = {
  [CSVErrorType.CSV_EMPTY]: "Your file doesn't have any rows.",
  [CSVErrorType.CSV_INVALID]:
    "Your file doesn't match the expected format. Please download the template CSV file, and follow the provided format.",
  [CSVErrorType.TOO_MANY_RECORDS]: "Your file can't have more than 50 rows.",
  // Should never occur, but just in case:
  [CSVErrorType.CSV_NOT_FOUND]: 'Your file could not be found. Please try again.'
};
